import type { Bereich } from './bereiche'
import { bereichsname, bereichszweck, unterpunktname } from './bereiche'
import { t, anzahlwort } from './sprache'

/**
 * Der ehrliche leere Bildschirm.
 *
 * 7.1 verbietet die leere Seite: Wo nichts ist, sagt das System, was hier
 * stehen wird und woher es kommt. Ein Bereich, der beim Anklicken nichts tut,
 * ist schlimmer als einer, der gar nicht in der Leiste steht — er lässt den
 * Anwender glauben, er habe etwas falsch gemacht.
 *
 * Absichtlich kein „coming soon" und keine Baustellengrafik: Der Satz nennt,
 * wie jede Meldung, drei Dinge — was ist, wie viel es ist, was zu tun ist
 * (R145).
 */
export function Platzhalter({
  bereich,
  unterpunkt,
  aufSprung,
}: {
  bereich: Bereich
  unterpunkt: string | null
  aufSprung: (bereich: string, unterpunkt?: string) => void
}) {
  const punkt = bereich.unterpunkte.find((u) => u.id === unterpunkt) ?? null
  const gebaut = bereich.unterpunkte.filter((u) => u.gebaut)

  return (
    <section className="platzhalter">
      <h1>{punkt ? unterpunktname(punkt) : bereichsname(bereich)}</h1>
      <p className="erlaeuterung">{bereichszweck(bereich)}</p>

      <div className="meldung neutral">
        <h2>{t('platzhalter.titel')}</h2>
        <p>{t('platzhalter.herkunft', { herkunft: bereich.herkunft })}</p>
        <p>
          {t('platzhalter.stand', {
            gesamt: anzahlwort(bereich.unterpunkte.length),
            gebaut: anzahlwort(gebaut.length),
            // Die Zahl entscheidet über das Verb, und das Verb steht im
            // Deutschen hinter der Zahl, im Englischen davor. Deshalb
            // benannte Platzhalter statt Stellennummern.
            verb: t(
              gebaut.length === 1
                ? 'platzhalter.stand.verb.eins'
                : 'platzhalter.stand.verb.mehrere',
            ),
          })}
        </p>
        {gebaut.length > 0 && (
          <p className="handreichung">
            {t('platzhalter.fertig')}{' '}
            {gebaut.map((u, i) => (
              <span key={u.id}>
                {i > 0 && ', '}
                <button type="button" className="ortsverweis" onClick={() => aufSprung(bereich.id, u.id)}>
                  {unterpunktname(u)}
                </button>
              </span>
            ))}
            .
          </p>
        )}
      </div>

      {bereich.unterpunkte.length > 0 && (
        <>
          <h2 className="abschnitt">{t('platzhalter.vorschau')}</h2>
          <ul className="vorschau">
            {bereich.unterpunkte.map((u) => (
              <li key={u.id} className={u.gebaut ? 'fertig' : ''}>
                <button
                  type="button"
                  className="ortsverweis"
                  onClick={() => aufSprung(bereich.id, u.id)}
                >
                  {unterpunktname(u)}
                </button>
                {!u.gebaut && <span className="ungebaut">{t('leiste.geplant')}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
